import { useEffect, useMemo, useState } from "react";
import { api } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Factory, Plus, Pencil, Trash2, Package, AlertTriangle, X, Clock, Euro, TrendingUp,
  BookMarked, Bookmark, FileDown, Link2, Search, Lightbulb, PlayCircle,
} from "lucide-react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import MaterialAutocomplete from "@/components/MaterialAutocomplete";
import { MaterialFormFields, emptyMaterial, computeMaterialCost, materialFromInventory } from "@/components/orders/MaterialFormFields";

const STATUS_LABELS = { offen: "Offen", in_bearbeitung: "In Bearbeitung", erledigt: "Erledigt" };
const STATUS_COLORS = {
  offen: "bg-blue-100 text-blue-700",
  in_bearbeitung: "bg-amber-100 text-amber-800",
  erledigt: "bg-green-100 text-green-700",
};

const emptyForm = () => ({
  name: "",
  menge: 1,
  einheit: "Stück",
  verkaufspreis_pro_einheit: 0,
  status: "offen",
  time_minutes: 0,
  notes: "",
  charge_nummer: "",
  linked_order_id: "",
  job_type: "produktion", // "entwicklung" | "produktion"
  materialien: [],
});

const ProductionPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [currentMaterial, setCurrentMaterial] = useState(emptyMaterial());
  const [editingMaterialIndex, setEditingMaterialIndex] = useState(-1);
  const [lagerMaterials, setLagerMaterials] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, job: null });
  const [search, setSearch] = useState("");
  const [jobTypeFilter, setJobTypeFilter] = useState("all"); // 'all' | 'entwicklung' | 'produktion'
  // Templates
  const [templates, setTemplates] = useState([]);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [confirmDeleteTemplate, setConfirmDeleteTemplate] = useState({ open: false, tpl: null });
  // Orders for linking
  const [orders, setOrders] = useState([]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (jobTypeFilter !== "all") params.set("job_type", jobTypeFilter);
      const qs = params.toString();
      const res = await api.get(`/api/production${qs ? "?" + qs : ""}`);
      setJobs(res.data || []);
    } catch (err) {
      toast.error("Fehler beim Laden der Produktions-Aufträge");
    } finally {
      setLoading(false);
    }
  };

  const fetchLager = async () => {
    try {
      const res = await api.get("/api/materials");
      setLagerMaterials(res.data || []);
    } catch { /* ignore */ }
  };

  const fetchTemplates = async () => {
    try {
      const res = await api.get("/api/production-templates");
      setTemplates(res.data || []);
    } catch { /* ignore */ }
  };

  const fetchOrders = async () => {
    try {
      const res = await api.get("/api/work-orders");
      setOrders(res.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchJobs(); fetchLager(); fetchTemplates(); fetchOrders(); }, []);
  useEffect(() => {
    const t = setTimeout(fetchJobs, 300);
    return () => clearTimeout(t);
  }, [search, jobTypeFilter]);

  const openCreate = (jobType = "produktion") => {
    setEditing(null);
    setForm({ ...emptyForm(), job_type: jobType });
    setCurrentMaterial(emptyMaterial());
    setEditingMaterialIndex(-1);
    setDialogOpen(true);
  };

  const openEdit = (job) => {
    setEditing(job);
    setForm({
      name: job.name || "",
      menge: job.menge || 1,
      einheit: job.einheit || "Stück",
      verkaufspreis_pro_einheit: job.verkaufspreis_pro_einheit || 0,
      status: job.status || "offen",
      time_minutes: job.time_minutes || 0,
      notes: job.notes || "",
      charge_nummer: job.charge_nummer || "",
      linked_order_id: job.linked_order_id || "",
      job_type: job.job_type || "produktion",
      // Convert stored `anzahl` (× menge) back to per-unit for editing UI
      materialien: (job.materialien || []).map(m => ({
        ...m,
        anzahl: String(m.anzahl_pro_einheit ?? m.anzahl ?? ""),
      })),
    });
    setCurrentMaterial(emptyMaterial());
    setEditingMaterialIndex(-1);
    setDialogOpen(true);
  };

  const applyTemplate = async (tpl) => {
    if (!tpl) return;
    setForm(f => ({
      ...f,
      name: f.name || tpl.name || "",
      einheit: tpl.einheit || f.einheit,
      verkaufspreis_pro_einheit: tpl.verkaufspreis_pro_einheit || f.verkaufspreis_pro_einheit,
      time_minutes: tpl.time_minutes || f.time_minutes,
      notes: tpl.notes || f.notes,
      // Templates store per-unit anzahl; keep as anzahl string for UI (MaterialFormFields expects strings)
      materialien: (tpl.materialien || []).map(m => ({
        ...m,
        anzahl: String(m.anzahl_pro_einheit ?? m.anzahl ?? ""),
      })),
    }));
    toast.success(`Vorlage "${tpl.name}" übernommen`);
    // Non-blocking usage bump
    api.post(`/api/production-templates/${tpl.id}/use`).catch(() => {});
    fetchTemplates();
  };

  const saveAsTemplate = async () => {
    if (!templateName.trim()) { toast.error("Vorlagen-Name erforderlich"); return; }
    if (form.materialien.length === 0) { toast.error("Keine Materialien in der Vorlage"); return; }
    try {
      await api.post("/api/production-templates", {
        name: templateName.trim(),
        einheit: form.einheit,
        verkaufspreis_pro_einheit: parseFloat(form.verkaufspreis_pro_einheit) || 0,
        time_minutes: parseInt(form.time_minutes) || 0,
        notes: form.notes || "",
        materialien: form.materialien.map(m => ({
          material_id: m.material_id || "",
          bezeichnung: m.bezeichnung || "",
          artikelnummer: m.artikelnummer || "",
          einheit: m.einheit || "Stück",
          anzahl_pro_einheit: parseFloat(m.anzahl) || 0,
          einkaufspreis: parseFloat(m.einkaufspreis) || 0,
          einzelpreis: parseFloat(m.einzelpreis) || 0,
          marge_prozent: parseFloat(m.marge_prozent) || 0,
        })),
      });
      toast.success(`Vorlage "${templateName}" gespeichert`);
      setSaveTemplateOpen(false);
      setTemplateName("");
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const deleteTemplate = async () => {
    const t = confirmDeleteTemplate.tpl;
    if (!t) return;
    try {
      await api.delete(`/api/production-templates/${t.id}`);
      toast.success("Vorlage gelöscht");
      setConfirmDeleteTemplate({ open: false, tpl: null });
      fetchTemplates();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  const copyToLinkedOrder = async (job) => {
    if (!job.linked_order_id) return;
    try {
      const res = await api.post(`/api/production/${job.id}/copy-to-order`);
      toast.success(`In Auftrag übertragen (${res.data.materials_transferred} Positionen)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Übertragung fehlgeschlagen");
    }
  };

  const spawnProduction = async (devJob) => {
    try {
      const res = await api.post(`/api/production/${devJob.id}/spawn-production`);
      toast.success(`Produktionsauftrag erstellt: ${res.data.charge_nummer}`);
      fetchJobs();
      fetchLager();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Übernahme fehlgeschlagen");
    }
  };

  const generatePdf = (job) => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let y = 15;
      // Header
      doc.setFontSize(18); doc.setFont("helvetica", "bold");
      doc.text("FERTIGUNGSSCHEIN", 14, y); y += 8;
      doc.setFontSize(10); doc.setFont("helvetica", "normal");
      doc.text(`Charge: ${job.charge_nummer || "-"}`, 14, y);
      doc.text(`Datum: ${new Date().toLocaleDateString("de-DE")}`, pageWidth - 60, y); y += 6;
      doc.setDrawColor(180); doc.line(14, y, pageWidth - 14, y); y += 6;
      // Job header
      doc.setFontSize(14); doc.setFont("helvetica", "bold");
      doc.text(job.name, 14, y); y += 7;
      doc.setFontSize(10); doc.setFont("helvetica", "normal");
      doc.text(`Menge: ${job.menge} ${job.einheit}`, 14, y);
      doc.text(`Status: ${STATUS_LABELS[job.status] || job.status}`, 80, y);
      if (job.verkaufspreis_pro_einheit > 0) {
        doc.text(`VK / Einheit: ${job.verkaufspreis_pro_einheit.toFixed(2)} €`, 140, y);
      }
      y += 6;
      if (job.linked_order_customer_name) {
        doc.text(`Kunde: ${job.linked_order_customer_name}${job.linked_order_auftragsnummer ? " (Auftrag " + job.linked_order_auftragsnummer + ")" : ""}`, 14, y);
        y += 6;
      }
      // VIN placeholder for user to fill in manually
      doc.text("VIN / Fahrgestell-Nr.: ______________________________________", 14, y); y += 8;

      // Materialien table
      const rows = (job.materialien || []).map((m) => ([
        m.bezeichnung || "",
        m.artikelnummer || "",
        `${m.anzahl_pro_einheit ?? m.anzahl ?? 0} ${m.einheit || ""}`,
        `${(parseFloat(m.anzahl) || 0).toFixed(2)} ${m.einheit || ""}`,
        (parseFloat(m.einkaufspreis) || 0).toFixed(2),
        (parseFloat(m.einzelpreis) || 0).toFixed(2),
      ]));
      doc.autoTable({
        startY: y,
        head: [["Bezeichnung", "Art.Nr.", "Pro Einheit", "Gesamt", "EK €", "VK €"]],
        body: rows,
        headStyles: { fillColor: [50, 50, 50], textColor: 255, fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        styles: { cellPadding: 2 },
        theme: "grid",
      });
      y = doc.lastAutoTable.finalY + 8;
      // Cost summary
      doc.setFontSize(10); doc.setFont("helvetica", "bold");
      doc.text("Kalkulation:", 14, y); y += 6;
      doc.setFont("helvetica", "normal");
      doc.text(`Materialkosten (EK): ${(job.total_material_ek || 0).toFixed(2)} €`, 14, y); y += 5;
      if (job.time_minutes > 0) {
        doc.text(`Arbeitszeit: ${Math.floor(job.time_minutes / 60)}h ${job.time_minutes % 60}min  (${(job.arbeitskosten || 0).toFixed(2)} €)`, 14, y); y += 5;
      }
      if (job.total_verkauf > 0) {
        doc.text(`Verkauf gesamt: ${(job.total_verkauf || 0).toFixed(2)} €`, 14, y); y += 5;
        doc.text(`Marge: ${(job.marge_absolut || 0).toFixed(2)} €`, 14, y); y += 5;
      }
      y += 4;
      // Checklist
      doc.setFont("helvetica", "bold"); doc.text("Prüfschritte:", 14, y); y += 6;
      doc.setFont("helvetica", "normal");
      [
        "☐ Material vollständig",
        "☐ Fertigung abgeschlossen",
        "☐ Funktionsprüfung durchgeführt",
        "☐ Verpackung / Etikettierung",
      ].forEach(line => { doc.text(line, 14, y); y += 5; });
      if (job.notes) {
        y += 4; doc.setFont("helvetica", "bold"); doc.text("Bemerkungen:", 14, y); y += 5;
        doc.setFont("helvetica", "normal");
        const noteLines = doc.splitTextToSize(job.notes, pageWidth - 28);
        doc.text(noteLines, 14, y);
      }
      // Signature
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setDrawColor(0); doc.line(14, pageHeight - 25, 90, pageHeight - 25);
      doc.text("Datum / Unterschrift Ausführender", 14, pageHeight - 20);
      doc.line(120, pageHeight - 25, pageWidth - 14, pageHeight - 25);
      doc.text("Datum / Unterschrift Abnahme", 120, pageHeight - 20);
      doc.save(`Fertigungsschein_${(job.charge_nummer || job.name).replace(/[^\w-]/g, "_")}.pdf`);
      toast.success("Fertigungsschein exportiert");
    } catch (err) {
      toast.error("PDF-Export fehlgeschlagen: " + (err.message || err));
    }
  };

  // Add or update material in local form list (anzahl in currentMaterial = per unit)
  const addOrUpdateMaterial = () => {
    if (!currentMaterial.bezeichnung && !currentMaterial.material_id) {
      toast.error("Bitte Material auswählen oder eingeben");
      return;
    }
    if (!parseFloat(currentMaterial.anzahl)) {
      toast.error("Bitte Anzahl pro Einheit angeben");
      return;
    }
    const mat = computeMaterialCost({ ...currentMaterial });
    if (editingMaterialIndex >= 0) {
      const next = [...form.materialien];
      next[editingMaterialIndex] = mat;
      setForm({ ...form, materialien: next });
    } else {
      setForm({ ...form, materialien: [...form.materialien, mat] });
    }
    setCurrentMaterial(emptyMaterial());
    setEditingMaterialIndex(-1);
  };

  const editMaterial = (idx) => {
    setEditingMaterialIndex(idx);
    setCurrentMaterial({ ...form.materialien[idx] });
  };

  const removeMaterial = (idx) => {
    setForm({ ...form, materialien: form.materialien.filter((_, i) => i !== idx) });
    if (editingMaterialIndex === idx) {
      setEditingMaterialIndex(-1);
      setCurrentMaterial(emptyMaterial());
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error("Bezeichnung erforderlich"); return; }
    const menge = parseFloat(form.menge) || 1;
    const payload = {
      name: form.name.trim(),
      menge,
      einheit: form.einheit,
      verkaufspreis_pro_einheit: parseFloat(form.verkaufspreis_pro_einheit) || 0,
      status: form.status,
      time_minutes: parseInt(form.time_minutes) || 0,
      notes: form.notes,
      charge_nummer: (form.charge_nummer || "").trim(),
      linked_order_id: form.linked_order_id || null,
      job_type: form.job_type || "produktion",
      materialien: form.materialien.map(m => ({
        material_id: m.material_id || "",
        bezeichnung: m.bezeichnung || "",
        artikelnummer: m.artikelnummer || "",
        einheit: m.einheit || "Stück",
        anzahl_pro_einheit: parseFloat(m.anzahl) || 0,
        einkaufspreis: parseFloat(m.einkaufspreis) || 0,
        einzelpreis: parseFloat(m.einzelpreis) || 0,
        marge_prozent: parseFloat(m.marge_prozent) || 0,
      })),
    };
    try {
      if (editing) {
        await api.put(`/api/production/${editing.id}`, payload);
        toast.success("Produktions-Auftrag aktualisiert");
      } else {
        await api.post("/api/production", payload);
        toast.success("Produktions-Auftrag erstellt");
      }
      setDialogOpen(false);
      fetchJobs();
      fetchLager();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleDelete = async () => {
    const job = confirmDelete.job;
    if (!job) return;
    try {
      await api.delete(`/api/production/${job.id}`);
      toast.success("Produktions-Auftrag gelöscht");
      setConfirmDelete({ open: false, job: null });
      fetchJobs();
      fetchLager();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  // Low-stock warnings for materials in the current form
  const stockWarnings = useMemo(() => {
    if (!form.materialien || !lagerMaterials.length) return [];
    const menge = parseFloat(form.menge) || 1;
    const map = Object.fromEntries(lagerMaterials.map(m => [m.id, m]));
    return form.materialien.map(fm => {
      if (!fm.material_id) return null;
      const inv = map[fm.material_id];
      if (!inv || inv.is_order_specific || inv.item_type === "dienstleistung") return null;
      const needed = (parseFloat(fm.anzahl) || 0) * menge;
      const available = (inv.bestand || 0) - (inv.bestand_reserviert || 0);
      const kritisch = needed > available;
      const nearMin = !kritisch && inv.min_bestand > 0 && (available - needed) <= inv.min_bestand;
      if (!kritisch && !nearMin) return null;
      return { bezeichnung: fm.bezeichnung, needed, available, min: inv.min_bestand, kritisch };
    }).filter(Boolean);
  }, [form.materialien, form.menge, lagerMaterials]);

  const stats = useMemo(() => {
    const total = jobs.length;
    const open = jobs.filter(j => j.status !== "erledigt").length;
    const done = jobs.filter(j => j.status === "erledigt").length;
    const totalMarge = jobs.filter(j => j.status === "erledigt").reduce((s, j) => s + (j.marge_absolut || 0), 0);
    return { total, open, done, totalMarge };
  }, [jobs]);

  return (
    <div className="space-y-6" data-testid="production-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2" style={{ fontFamily: "Barlow Condensed" }}>
            <Factory className="w-7 h-7" /> Produktion
          </h1>
          <p className="text-sm text-muted-foreground">{stats.total} Fertigungsaufträge · {stats.open} offen · {stats.done} erledigt</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => openCreate("entwicklung")} className="gap-2" data-testid="new-development-btn">
            <Lightbulb className="w-4 h-4" /> Entwicklung
          </Button>
          <Button onClick={() => openCreate("produktion")} className="gap-2" data-testid="new-production-btn">
            <Plus className="w-4 h-4" /> Neue Produktion
          </Button>
        </div>
      </div>

      {/* Tab filter */}
      <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit" data-testid="job-type-tabs">
        {[
          { key: "all", label: `Alle (${jobs.length})`, icon: Factory },
          { key: "entwicklung", label: `Entwicklung (${jobs.filter(j => j.job_type === "entwicklung").length})`, icon: Lightbulb },
          { key: "produktion", label: `Produktion (${jobs.filter(j => (j.job_type || "produktion") === "produktion").length})`, icon: Factory },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setJobTypeFilter(key)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${jobTypeFilter === key ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            data-testid={`job-type-tab-${key}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Suche: Name, Charge-Nummer, Notiz..."
          className="pl-9"
          data-testid="production-search"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-card border rounded-xl p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Factory className="w-4 h-4" />Gesamt</div>
          <p className="text-2xl font-bold mt-1">{stats.total}</p>
        </div>
        <div className="bg-card border rounded-xl p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Clock className="w-4 h-4" />Offen</div>
          <p className="text-2xl font-bold mt-1">{stats.open}</p>
        </div>
        <div className="bg-card border rounded-xl p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Package className="w-4 h-4" />Erledigt</div>
          <p className="text-2xl font-bold mt-1">{stats.done}</p>
        </div>
        <div className="bg-card border rounded-xl p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><TrendingUp className="w-4 h-4" />Marge (erledigt)</div>
          <p className="text-2xl font-bold mt-1">{stats.totalMarge.toFixed(2)} €</p>
        </div>
      </div>

      {/* Job list */}
      <div className="space-y-3">
        {loading ? (
          <p className="text-muted-foreground text-center py-6">Wird geladen…</p>
        ) : jobs.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed rounded-lg text-muted-foreground">
            <Factory className="w-12 h-12 mx-auto mb-2 opacity-40" />
            <p>Noch keine Produktions-Aufträge.</p>
            <Button variant="outline" size="sm" onClick={() => openCreate("produktion")} className="mt-3 gap-2">
              <Plus className="w-4 h-4" /> Ersten Auftrag anlegen
            </Button>
          </div>
        ) : (
          jobs.map((j) => (
            <Card key={j.id} className="hover:shadow-md transition-shadow" data-testid={`production-row-${j.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {j.job_type === "entwicklung" && (
                        <Badge className="bg-purple-100 text-purple-700 gap-1" data-testid="dev-badge">
                          <Lightbulb className="w-3 h-3" /> Entwicklung
                        </Badge>
                      )}
                      <h3 className="font-semibold text-lg">{j.name}</h3>
                      <Badge className={STATUS_COLORS[j.status] || ""} data-testid={`status-badge-${j.id}`}>
                        {STATUS_LABELS[j.status] || j.status}
                      </Badge>
                      <Badge variant="outline">{j.menge} {j.einheit}</Badge>
                      {j.charge_nummer && (
                        <Badge variant="outline" className="bg-slate-50" title="Chargen-/Serien-Nummer">
                          {j.charge_nummer}
                        </Badge>
                      )}
                      {j.parent_job_name && (
                        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 gap-1" title="Aus Entwicklung übernommen">
                          <Lightbulb className="w-3 h-3" /> aus &quot;{j.parent_job_name}&quot;
                        </Badge>
                      )}
                      {j.spawned_production_ids && j.spawned_production_ids.length > 0 && (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 gap-1" title="Bereits zur Produktion übernommen">
                          <PlayCircle className="w-3 h-3" /> {j.spawned_production_ids.length}× produziert
                        </Badge>
                      )}
                      {j.linked_order_id && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 gap-1" title="Verknüpfter Kundenauftrag">
                          <Link2 className="w-3 h-3" />
                          {j.linked_order_auftragsnummer ? `#${j.linked_order_auftragsnummer}` : "Auftrag"}
                          {j.linked_order_customer_name ? ` · ${j.linked_order_customer_name}` : ""}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                      <span>Material: <strong className="text-foreground">{j.total_material_ek.toFixed(2)} €</strong> (EK)</span>
                      {j.time_minutes > 0 && (
                        <span>Arbeit: <strong className="text-foreground">{Math.floor(j.time_minutes / 60)}h {j.time_minutes % 60}min</strong> ({j.arbeitskosten.toFixed(2)} €)</span>
                      )}
                      {j.verkaufspreis_pro_einheit > 0 && (
                        <span>Verkauf: <strong className="text-foreground">{j.total_verkauf.toFixed(2)} €</strong></span>
                      )}
                      {j.verkaufspreis_pro_einheit > 0 && (
                        <span className={j.marge_absolut >= 0 ? "text-green-700" : "text-red-700"}>
                          Marge: <strong>{j.marge_absolut.toFixed(2)} €</strong>
                        </span>
                      )}
                      <span>{j.materialien?.length || 0} Position(en)</span>
                    </div>
                    {j.notes && (
                      <p className="text-xs text-muted-foreground mt-1 italic line-clamp-1">&quot;{j.notes}&quot;</p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {j.job_type === "entwicklung" && (
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => spawnProduction(j)}
                        title="Zur Produktion übernehmen"
                        className="text-green-700 hover:text-green-800 hover:bg-green-50"
                        data-testid={`spawn-production-${j.id}`}
                      >
                        <PlayCircle className="w-4 h-4" />
                      </Button>
                    )}
                    {j.linked_order_id && (
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => copyToLinkedOrder(j)}
                        title="In verknüpften Auftrag übertragen"
                        className="text-blue-600 hover:text-blue-700"
                        data-testid={`copy-to-order-${j.id}`}
                      >
                        <Link2 className="w-4 h-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => generatePdf(j)} title="Fertigungsschein PDF" data-testid={`pdf-production-${j.id}`}>
                      <FileDown className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(j)} data-testid={`edit-production-${j.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setConfirmDelete({ open: true, job: j })} className="text-destructive" data-testid={`delete-production-${j.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="production-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Barlow Condensed" }}>
              {editing
                ? (form.job_type === "entwicklung" ? "Entwicklung bearbeiten" : "Produktions-Auftrag bearbeiten")
                : (form.job_type === "entwicklung" ? "Neue Entwicklung" : "Neuer Produktions-Auftrag")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Job-Type Toggle */}
            <div className="flex gap-2 p-1 bg-muted rounded-lg" data-testid="job-type-toggle">
              <button
                type="button"
                onClick={() => setForm({ ...form, job_type: "entwicklung" })}
                className={`flex-1 inline-flex items-center justify-center gap-2 py-1.5 rounded-md text-sm font-medium transition-colors ${form.job_type === "entwicklung" ? "bg-background shadow-sm text-purple-700" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="job-type-entwicklung"
              >
                <Lightbulb className="w-4 h-4" /> Entwicklung (Planung, kein Bestand)
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, job_type: "produktion" })}
                className={`flex-1 inline-flex items-center justify-center gap-2 py-1.5 rounded-md text-sm font-medium transition-colors ${form.job_type === "produktion" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="job-type-produktion"
              >
                <Factory className="w-4 h-4" /> Produktion (Bestand wird ausgebucht)
              </button>
            </div>
            {form.job_type === "entwicklung" && (
              <div className="text-xs bg-purple-50 border border-purple-200 rounded-md px-3 py-2 text-purple-800">
                In der Entwicklung wird kein Lagerbestand reserviert oder ausgebucht. Materialien dienen als BOM/Rezept.
                Verknüpfung mit einem Kundenauftrag ist Pflicht (für Zeit-Abrechnung). Aus der abgeschlossenen Entwicklung kannst du per Klick einen Produktionsauftrag erstellen.
              </div>
            )}
            {/* Template picker + Save-as-Template */}
            {!editing && templates.length > 0 && (
              <div className="p-3 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50">
                <div className="flex items-center gap-2 mb-2 text-sm font-medium">
                  <BookMarked className="w-4 h-4" /> Vorlage / Rezept übernehmen
                </div>
                <div className="flex flex-wrap gap-2">
                  {templates.slice(0, 6).map(t => (
                    <div key={t.id} className="flex items-center gap-1 bg-white border rounded-md text-xs pl-2 hover:border-primary transition-colors">
                      <button
                        type="button"
                        onClick={() => applyTemplate(t)}
                        className="py-1.5 pr-2 hover:text-primary text-left"
                        data-testid={`template-${t.id}`}
                        title={`${t.materialien?.length || 0} Position(en) · ${t.usage_count || 0}× verwendet`}
                      >
                        {t.name}
                        {t.usage_count > 0 && <span className="ml-1 text-muted-foreground">({t.usage_count}×)</span>}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteTemplate({ open: true, tpl: t })}
                        className="p-1 text-muted-foreground hover:text-destructive"
                        title="Vorlage löschen"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label>Bezeichnung *</Label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="z.B. Kabelbaum VW T4, Steuergerät-Umbau, Vormontage Sicherung"
                data-testid="production-name"
                autoFocus
              />
            </div>

            {/* Charge & optional order link */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Chargen-/Serien-Nr.</Label>
                <Input
                  value={form.charge_nummer}
                  onChange={e => setForm({ ...form, charge_nummer: e.target.value })}
                  placeholder={editing ? "" : "leer = automatisch CH-YYYYMM-XXXXX"}
                  data-testid="production-charge"
                />
              </div>
              <div>
                <Label className="flex items-center gap-1"><Link2 className="w-3 h-3" /> Verknüpfter Auftrag (optional)</Label>
                <Select
                  value={form.linked_order_id || "__none__"}
                  onValueChange={(v) => setForm({ ...form, linked_order_id: v === "__none__" ? "" : v })}
                >
                  <SelectTrigger data-testid="production-linked-order">
                    <SelectValue placeholder="Kein Auftrag verknüpft" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="__none__">— Kein Auftrag —</SelectItem>
                    {orders.map(o => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.auftragsnummer ? `#${o.auftragsnummer} · ` : ""}{o.customer_name || "?"} · {o.description?.slice(0, 40) || ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <Label>Anzahl Einheiten *</Label>
                <Input
                  type="number" min="0.01" step="0.01"
                  value={form.menge}
                  onChange={e => setForm({ ...form, menge: e.target.value })}
                  data-testid="production-menge"
                />
              </div>
              <div>
                <Label>Einheit</Label>
                <Input
                  value={form.einheit}
                  onChange={e => setForm({ ...form, einheit: e.target.value })}
                  data-testid="production-einheit"
                />
              </div>
              <div>
                <Label>VK / Einheit (€)</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={form.verkaufspreis_pro_einheit}
                  onChange={e => setForm({ ...form, verkaufspreis_pro_einheit: e.target.value })}
                  data-testid="production-vk"
                />
              </div>
              <div>
                <Label>Arbeit (min)</Label>
                <Input
                  type="number" min="0" step="1"
                  value={form.time_minutes}
                  onChange={e => setForm({ ...form, time_minutes: e.target.value })}
                  data-testid="production-time"
                />
              </div>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="production-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="offen">Offen (Material reserviert)</SelectItem>
                  <SelectItem value="in_bearbeitung">In Bearbeitung</SelectItem>
                  <SelectItem value="erledigt">Erledigt (Material wird ausgebucht)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Materials block */}
            <div className="border rounded-lg p-3 bg-muted/20 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2"><Package className="w-4 h-4" /> Materialien (pro Einheit)</Label>
                <span className="text-xs text-muted-foreground">Pro Auftrag wird ×{parseFloat(form.menge) || 1} ausgebucht</span>
              </div>

              {/* Existing rows */}
              {form.materialien.length > 0 && (
                <div className="space-y-1.5">
                  {form.materialien.map((m, idx) => {
                    const total = (parseFloat(m.anzahl) || 0) * (parseFloat(form.menge) || 1);
                    return (
                      <div key={idx} className={`text-xs bg-white border rounded p-2 flex items-center gap-2 ${editingMaterialIndex === idx ? "ring-2 ring-primary" : ""}`}>
                        <Package className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                        <span className="font-medium flex-1 truncate">{m.bezeichnung}</span>
                        <span className="text-muted-foreground">{m.anzahl} {m.einheit} × {form.menge} = <strong className="text-foreground">{total.toFixed(2)}</strong></span>
                        {m.einzelpreis > 0 && <span className="text-green-700">{((parseFloat(m.einzelpreis) || 0) * total).toFixed(2)} €</span>}
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => editMaterial(idx)} data-testid={`edit-mat-row-${idx}`}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => removeMaterial(idx)} data-testid={`remove-mat-row-${idx}`}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Low-stock warnings */}
              {stockWarnings.length > 0 && (
                <div className="border-l-4 border-orange-400 bg-orange-50 p-2 rounded-r space-y-1 text-xs">
                  <div className="font-medium text-orange-800 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> Bestandshinweis
                  </div>
                  {stockWarnings.map((w, i) => (
                    <div key={i} className={w.kritisch ? "text-red-700" : "text-orange-700"}>
                      <strong>{w.bezeichnung}</strong>: benötigt {w.needed.toFixed(2)}, verfügbar {w.available.toFixed(2)}
                      {w.kritisch ? " — Fehlbestand!" : ` (nach Auftrag unter Mindestbestand ${w.min})`}
                    </div>
                  ))}
                </div>
              )}

              {/* Add material form */}
              <div className="border-t pt-3 space-y-3">
                <div>
                  <Label className="text-xs">Material suchen</Label>
                  <MaterialAutocomplete
                    value={currentMaterial.bezeichnung}
                    onChange={(v) => setCurrentMaterial({ ...currentMaterial, bezeichnung: v, material_id: "" })}
                    onSelect={(m) => setCurrentMaterial(materialFromInventory(m, currentMaterial))}
                    placeholder="Material aus Lager oder neu eingeben..."
                  />
                </div>
                <MaterialFormFields material={currentMaterial} setMaterial={setCurrentMaterial} testIdPrefix="prod-current-mat" />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={editingMaterialIndex >= 0 ? "default" : "outline"}
                    size="sm"
                    className="flex-1 gap-2"
                    onClick={addOrUpdateMaterial}
                    data-testid="add-material-btn"
                  >
                    <Plus className="w-4 h-4" />
                    {editingMaterialIndex >= 0 ? "Material aktualisieren" : "Material hinzufügen"}
                  </Button>
                  {editingMaterialIndex >= 0 && (
                    <Button variant="outline" size="sm" onClick={() => { setEditingMaterialIndex(-1); setCurrentMaterial(emptyMaterial()); }}>
                      Abbrechen
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div>
              <Label>Notiz / Beschreibung</Label>
              <Textarea
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional: Bemerkungen, Prüfschritte, Referenz"
                data-testid="production-notes"
                rows={2}
              />
            </div>

            {/* Live totals */}
            <div className="border rounded-lg p-3 bg-slate-50 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <div className="text-muted-foreground">Materialkosten EK</div>
                <div className="text-lg font-bold">
                  {form.materialien.reduce((s, m) => s + (parseFloat(m.einkaufspreis) || 0) * (parseFloat(m.anzahl) || 0), 0).toFixed(2)} × {parseFloat(form.menge) || 1} = {(form.materialien.reduce((s, m) => s + (parseFloat(m.einkaufspreis) || 0) * (parseFloat(m.anzahl) || 0), 0) * (parseFloat(form.menge) || 1)).toFixed(2)} €
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Materialverkauf (VK)</div>
                <div className="text-lg font-bold">
                  {(form.materialien.reduce((s, m) => s + (parseFloat(m.einzelpreis) || 0) * (parseFloat(m.anzahl) || 0), 0) * (parseFloat(form.menge) || 1)).toFixed(2)} €
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Verkauf gesamt</div>
                <div className="text-lg font-bold">
                  {((parseFloat(form.verkaufspreis_pro_einheit) || 0) * (parseFloat(form.menge) || 1)).toFixed(2)} €
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Marge (VK - EK)</div>
                <div className="text-lg font-bold">
                  {(
                    ((parseFloat(form.verkaufspreis_pro_einheit) || 0) * (parseFloat(form.menge) || 1))
                    - (form.materialien.reduce((s, m) => s + (parseFloat(m.einkaufspreis) || 0) * (parseFloat(m.anzahl) || 0), 0) * (parseFloat(form.menge) || 1))
                  ).toFixed(2)} €
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
            {form.materialien.length > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => { setTemplateName(form.name || ""); setSaveTemplateOpen(true); }}
                className="gap-2 mr-auto"
                data-testid="save-as-template-btn"
              >
                <Bookmark className="w-4 h-4" /> Als Vorlage speichern
              </Button>
            )}
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSubmit} className="gap-2" data-testid="submit-production-btn">
              <Euro className="w-4 h-4" /> {editing ? "Speichern" : "Auftrag erstellen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save as Template Dialog */}
      <Dialog open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen}>
        <DialogContent className="sm:max-w-[440px]" data-testid="save-template-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Barlow Condensed" }}>
              <Bookmark className="w-5 h-5" /> Als Vorlage speichern
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Speichert Materialliste, VK, Arbeitszeit und Notiz als wiederverwendbare Vorlage. Anzahl-Einheiten und Chargen-Nummer werden <strong>nicht</strong> gespeichert.
            </p>
            <div>
              <Label>Vorlagen-Name *</Label>
              <Input
                value={templateName}
                onChange={e => setTemplateName(e.target.value)}
                placeholder="z.B. Kabelbaum Standard T4"
                data-testid="template-name-input"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveTemplateOpen(false)}>Abbrechen</Button>
            <Button onClick={saveAsTemplate} data-testid="save-template-confirm" className="gap-2">
              <Bookmark className="w-4 h-4" /> Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Template Confirm */}
      <AlertDialog open={confirmDeleteTemplate.open} onOpenChange={(v) => setConfirmDeleteTemplate({ ...confirmDeleteTemplate, open: v })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Vorlage löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{confirmDeleteTemplate.tpl?.name}&quot; wird dauerhaft entfernt. Bereits erstellte Aufträge bleiben erhalten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={deleteTemplate} className="bg-destructive hover:bg-destructive/90">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={confirmDelete.open} onOpenChange={(v) => setConfirmDelete({ ...confirmDelete, open: v })}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Produktions-Auftrag löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete.job?.status === "erledigt"
                ? "Der Auftrag ist bereits erledigt — Materialien bleiben ausgebucht."
                : "Reservierte Materialien werden wieder freigegeben."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90" data-testid="delete-confirm-ok">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductionPage;
